import * as React from "react";

import { useId, useIndex, useLayer } from "../../hooks";
import { PoseGroup } from "react-pose";

import Tooltip from "../../elements/Tooltip";

type WithTooltipProps = {
  children: React.ReactElement<any>;
  content: string;
  position?: "top" | "bottom" | "left" | "right";
  tooltipWidth?: any;
  framerWidth?: number;
  framerHeight?: number;
};

const OFFSET = 5;

function WithTooltip({
  children,
  content,
  position = "bottom",
  tooltipWidth,
  framerHeight = 1,
  framerWidth = 1
}: WithTooltipProps) {
  const id = useId();
  const zIndex = useIndex(id);
  const childRef = React.useRef<HTMLDivElement>();
  const { updateLayer, removeLayer, parentElement } = useLayer(id);

  const [showTooltip, setShowTooltip] = React.useState(false);
  const timeoutRef = React.useRef<number>();

  React.useEffect(() => {
    let top = 0;
    let left = 0;

    const box = childRef.current
      ? childRef.current.getBoundingClientRect()
      : {
          width: framerWidth,
          height: framerHeight,
          top: 0,
          left: 0,
          bottom: framerHeight,
          right: framerWidth
        };

    switch (position) {
      case "top":
        top = box.top - OFFSET;
        left = box.left + box.width / 2;
        break;
      case "bottom":
        top = box.bottom + OFFSET;
        left = box.left + box.width / 2;
        break;
      case "left":
        left = box.left - OFFSET;
        top = box.top + box.height / 2;
        break;
      case "right":
        left = box.right + OFFSET;
        top = box.top + box.height / 2;
        break;
      default:
        throw new Error("invalid tooltip position");
    }

    if (childRef.current && parentElement) {
      const parentBox = parentElement.getBoundingClientRect();
      top = top - parentBox.top;
      left = left - parentBox.left;
    }

    const style: React.CSSProperties = {
      zIndex,
      top,
      left,
      maxWidth: tooltipWidth || "auto"
    };

    updateLayer(
      <PoseGroup flipMove={false}>
        {showTooltip && (
          <Tooltip style={style} key={id} position={position}>
            {content}
          </Tooltip>
        )}
      </PoseGroup>
    );
  }, [content, showTooltip]);

  const handleMouseEnter = React.useCallback(() => {
    if (timeoutRef.current) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
      timeoutRef.current = null as any;
    }, 300);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null as any;
    }

    setShowTooltip(false);
  }, []);

  React.useEffect(() => {
    if (childRef.current) {
      childRef.current.addEventListener("mouseenter", handleMouseEnter);
      childRef.current.addEventListener("mouseleave", handleMouseLeave);
    } else {
      console.warn(
        "The WithTooltip component expected a child which exposes a 'ref' prop, but couldn't find one"
      );
    }

    return () => {
      removeLayer();
      if (childRef.current) {
        childRef.current.removeEventListener("mouseenter", handleMouseEnter);
        childRef.current.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  return React.cloneElement(children, {
    ref: childRef
  });
}

export default WithTooltip;
